import type React from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	createMarkdownProcessorMultiTags,
	extractMarkdownText,
	type StreamableComponentConfig,
} from '@atlassian/conversation-assistant-content-transformer/utils/stream-markdown-parser';
import type { LuminaSubAgentResponseType } from '@atlassian/lumina';
import { setSessionItem } from '@atlassian/search-dialog/session-storage-utils';
import type { SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';

import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { type QueryIntent, SEARCH_AI_ANSWER_CACHE_KEY } from '../../constants';
import type { IntentClassificationData } from '../../query-intent-api/types';
import { sha256Hash } from '../../sha256-hash';
import { StreamAnswerGenerationTypes } from '../../stream-api/stream-answer-generation-types';
import { type AIAnswerContextType } from '../ai-answer-context';
import { AIAnswerQueryStreamType } from '../ai-answer-query-stream-type';
import { DEFAULT_EMPTY_NLP_SEARCH_STATE } from '../default-empty-nlp-search-state';
import {
	getNonStreamFetchErrorEventPayload,
	getStreamFetchErrorEventPayload,
} from '../error-analytics';
import { NLPSearchErrorCodes } from '../nlp-search-error-codes';
import { NLPSearchErrorState } from '../nlp-search-error-state';
import { NLPSearchResultFormat } from '../nlp-search-result-format';
import type {
	AIAnswerQueryFunctionType,
	AIAnswerQueryStreamMarkdownFailedBackend,
	AIAnswerQueryStreamResponse,
	NLPDynamicLayoutSubAgentResponse,
	NLPSearchType,
	parsedFiltersType,
	SAINResearchStep,
} from '../types';

import { mergeResearchStepTrace } from './mergeResearchStepTrace';
import { checkSkipAIAnswerCall } from './smartAnswerRouteStrategy';
import { StreamFailedError } from './types';

export type FetchAIAnswerArgs = {
	cloudId: string;
	additional_context?: string;
	locale: string;
	query: string;
	source: string;
	followUpsEnabled?: boolean;
	getAIAnswer: AIAnswerQueryFunctionType | null;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	partialResponse: AIAnswerContextType['partialResponse'];
	partialResponseAbortControllerRef: React.MutableRefObject<AbortController | undefined>;
	setPartialResponse: React.Dispatch<React.SetStateAction<AIAnswerContextType['partialResponse']>>;
	setData: React.Dispatch<React.SetStateAction<NLPSearchType>>;
	setAnswerStreamed: React.Dispatch<React.SetStateAction<boolean>>;
	parsedFilters?: parsedFiltersType;
	selectedProduct?: string;
	isReadingAids: boolean;
	fireAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	setQueryReasoning: React.Dispatch<
		React.SetStateAction<AIAnswerContextType['queryReasoning']> | undefined
	>;
	/**
	 * Setter for the structured research-step state derived from
	 * `RESEARCH_STEP_*` trace messages.
	 */
	setResearchSteps?: React.Dispatch<React.SetStateAction<{ [stepId: string]: SAINResearchStep }>>;
	setDynamicLayoutSubAgentMessages: React.Dispatch<
		React.SetStateAction<NLPDynamicLayoutSubAgentResponse[] | LuminaSubAgentResponseType[]>
	>;
	setIsLuminaAnswer: React.Dispatch<boolean>;
	setLuminaContentMap: React.Dispatch<Record<string, string>>;
	experimentContext?: {
		experimentLayers: SearchExperimentLayer[];
	};
	queryIntentClassificationData?: IntentClassificationData;
	referrer?: string | null;
	searchSessionId?: string;
	sourceProduct?: string;
	queryIntent?: QueryIntent;
};

export const LUMINA_START_DELIMITER = '◐';
export const LUMINA_END_DELIMITER = '◑';
export const LUMINA_PLACEHOLDER_TAG = '\n\n[^lumina^]\n\n';
export const LUMINA_CONTENT_PREFIX = 'lumina-';

const getLuminaComponents = (): StreamableComponentConfig[] => [
	{
		key: 'lumina',
		startTag: LUMINA_START_DELIMITER,
		endTag: LUMINA_END_DELIMITER,
		placeholderTag: LUMINA_PLACEHOLDER_TAG,
		isEnabled: fg('kd_sain_enable_inline_lumina'),
	},
];

export const fetchAIAnswer = async ({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	cloudId,
	locale,
	query,
	additional_context,
	source,
	followUpsEnabled = false,
	getAIAnswer,
	setLoading,
	partialResponse,
	partialResponseAbortControllerRef,
	setPartialResponse,
	setData,
	setAnswerStreamed,
	parsedFilters,
	selectedProduct,
	isReadingAids,
	fireAnalyticsEvent,
	setQueryReasoning,
	setResearchSteps,
	setDynamicLayoutSubAgentMessages,
	setIsLuminaAnswer,
	setLuminaContentMap,
	experimentContext,
	queryIntentClassificationData,
	referrer,
	searchSessionId,
	sourceProduct,
	queryIntent,
}: FetchAIAnswerArgs): Promise<void> => {
	if (!getAIAnswer) {
		return;
	}
	if (partialResponse?.partialNlpResult) {
		// clean up any old partial responses
		setPartialResponse(undefined);
	}
	// end any old partial response streams
	partialResponseAbortControllerRef.current?.abort();
	const abortController = new AbortController();
	partialResponseAbortControllerRef.current = abortController;

	// Check if we can skip the AI answer call based on intent classification data.
	// If the intent already has the data we need (e.g., PersonIntent with persons[]),
	// use it directly instead of making another API call.
	if (fg('kd_fe_persons_intent_detection_els')) {
		const skipResult = checkSkipAIAnswerCall(queryIntentClassificationData);
		if (skipResult.skip) {
			setData(skipResult.data);
			setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({ query, data: skipResult.data }));
			setAnswerStreamed(true);
			setLoading(false);
			return;
		}
	}

	// For graphQL getAIAnswer fns we expect each product to pass their own cloudIdARI in order for this component to be more product agnostic
	const aiAnswer = getAIAnswer({
		variables: {
			query,
			additional_context,
			locale,
			filters: parsedFilters,
			selectedProduct,
			experience: isReadingAids ? 'readingAids' : source,
			followUpsEnabled,
			experimentContext,
			...(SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(sourceProduct) &&
			referrer === 'zeroQuery'
				? { isQuerySuggestion: true }
				: {}),
		},
	});

	// Legacy version of processStreamResponse - used when feature gate is off
	const processStreamResponse = async (answer: AIAnswerQueryStreamResponse) => {
		try {
			let next = await answer.stream.next();
			let answerFormat;
			// Local variable to track if answer is Lumina format (used for cache enablement)
			let isLuminaAnswerLocal = false;
			// Guard so the research-step failsafe only fires on the first AnswerPart
			let hasCompletedAllSteps = false;

			const processStreamMarkdownMultiTag = createMarkdownProcessorMultiTags(getLuminaComponents);
			const luminaContentMap: Record<string, string> = {};
			while (!next.done) {
				// setLoading needed here for instances where stream gets interrupted and restarts, ensuring loading state is set while stream is active
				setLoading(true);
				const streamEvent = next.value;

				if (streamEvent.type === AIAnswerQueryStreamType.AnswerType) {
					answerFormat = streamEvent.message.format;
				}
				if (
					streamEvent.type === AIAnswerQueryStreamType.GenerationType &&
					SAINFeatureControl.canRenderLumina()
				) {
					if (streamEvent.message.answer_generation_type === StreamAnswerGenerationTypes.LUMINA) {
						isLuminaAnswerLocal = true;
						setIsLuminaAnswer(true);
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.Trace) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}
					setQueryReasoning(streamEvent.message);
					// When the new FG is on and the BE sent a structured RESEARCH_STEP_*
					// trace, merge it into the local research-step state.
					if (
						streamEvent.traceData &&
						setResearchSteps &&
						(SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
							SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled())
					) {
						const traceData = streamEvent.traceData;
						setResearchSteps((prev) => mergeResearchStepTrace(prev, traceData));
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.AnswerPart) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}

					// Failsafe (mirrors Chat's `completeAllSteps`): when the answer
					// starts streaming, mark every research step complete in case the
					// BE skipped any `RESEARCH_STEP_COMPLETE` messages. Prevents the
					// shimmer animation from getting stuck on incomplete steps.
					if (
						!hasCompletedAllSteps &&
						setResearchSteps &&
						(SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
							SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled())
					) {
						hasCompletedAllSteps = true;
						setResearchSteps((prev) => {
							let mutated = false;
							const next: { [stepId: string]: SAINResearchStep } = {};
							for (const [id, step] of Object.entries(prev)) {
								if (!step.completed) {
									next[id] = { ...step, completed: true };
									mutated = true;
								} else {
									next[id] = step;
								}
							}
							return mutated ? next : prev;
						});
					}

					if (fg('kd_sain_enable_inline_lumina')) {
						const { processedText, markdownData } = processStreamMarkdownMultiTag(
							streamEvent.message.nlpResult,
						);

						if (Object.keys(markdownData).length > 0) {
							Object.entries(markdownData).forEach(([key, { content }]) => {
								luminaContentMap[key] = (luminaContentMap[key] ?? '') + content;
							});
							setLuminaContentMap({ ...luminaContentMap });
						}

						setPartialResponse((previousPartialResponse) => ({
							format: NLPSearchResultFormat.MARKDOWN,
							partialNlpResult: (previousPartialResponse?.partialNlpResult ?? '') + processedText,
						}));
					} else {
						setPartialResponse((previousPartialResponse) => {
							let previousPartialNlpResult: string =
								previousPartialResponse?.partialNlpResult || '';
							return {
								format: NLPSearchResultFormat.MARKDOWN,
								partialNlpResult: previousPartialNlpResult + streamEvent.message.nlpResult,
							};
						});
					}
				}

				if (SAINFeatureControl.canRenderLumina()) {
					if (streamEvent.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage) {
						setDynamicLayoutSubAgentMessages((prevMessages) => [
							...prevMessages,
							streamEvent.message,
						]);
					}
				}

				if (streamEvent.type === AIAnswerQueryStreamType.FinalResponse) {
					const authorityUiMetadata = streamEvent.message.authority_ui_metadata ?? null;

					if (fg('kd_sain_enable_inline_lumina')) {
						const { boundaryContents } = extractMarkdownText(
							streamEvent.message.nlpResult ?? '',
							LUMINA_START_DELIMITER,
							LUMINA_END_DELIMITER,
							LUMINA_PLACEHOLDER_TAG,
						);

						const luminaContentMap: Record<string, string> = {};
						boundaryContents.forEach((content, index) => {
							luminaContentMap[`${LUMINA_CONTENT_PREFIX}${index}`] = content;
						});

						setLuminaContentMap(luminaContentMap);
						setData({ ...streamEvent.message, authority_ui_metadata: authorityUiMetadata });
					} else {
						setData({ ...streamEvent.message, authority_ui_metadata: authorityUiMetadata });
					}
					setPartialResponse(undefined);
					setLoading(false);
				}

				next = await answer.stream.next();
				if (abortController.signal.aborted) {
					// eslint-disable-next-line no-throw-literal
					throw 'Aborted';
				}
			}

			const streamEnd = next.value;
			if (abortController.signal.aborted) {
				// eslint-disable-next-line no-throw-literal
				throw streamEnd;
			}

			if (streamEnd.state === 'complete') {
				if (fg('kd_sain_enable_inline_lumina')) {
					const { boundaryContents } = extractMarkdownText(
						streamEnd.value.message.nlpResult ?? '',
						LUMINA_START_DELIMITER,
						LUMINA_END_DELIMITER,
						LUMINA_PLACEHOLDER_TAG,
					);

					const luminaContentMap: Record<string, string> = {};
					boundaryContents.forEach((content, index) => {
						luminaContentMap[`${LUMINA_CONTENT_PREFIX}${index}`] = content;
					});

					setLuminaContentMap(luminaContentMap);
					setData(streamEnd.value.message);
				} else {
					setData(streamEnd.value.message);
				}

				setPartialResponse(undefined);
				// Only store to cache if not a Lumina answer
				if (isReadingAids || !isLuminaAnswerLocal) {
					setSessionItem(
						SEARCH_AI_ANSWER_CACHE_KEY,
						JSON.stringify({
							query: query,
							data: streamEnd.value.message,
						}),
					);
				}
			} else {
				if (streamEnd.reason === 'timeout') {
					setPartialResponse(undefined);
					setData({
						errorState: NLPSearchErrorState.TimeoutError,
						errorCode: 0,
						format: null,
						disclaimer: null,
						nlpResults: [],
						uniqueSources: [],
						nlpFollowUpResults: null,
					});
				} else {
					throw streamEnd;
				}
			}
		} catch (error: unknown) {
			fireAnalyticsEvent(getStreamFetchErrorEventPayload(error, source));
			if (error === 'Aborted') {
				return;
			}
			setPartialResponse(undefined);
			setData({
				errorState:
					typeof error === 'object' &&
					error !== null &&
					'details' in error &&
					error?.details === NLPSearchErrorCodes.HIPAA_ENABLED_ERROR
						? NLPSearchErrorState.HIPAAError
						: NLPSearchErrorState.NetworkError,
				errorCode: (error as AIAnswerQueryStreamMarkdownFailedBackend)?.errorCode
					? (error as AIAnswerQueryStreamMarkdownFailedBackend).errorCode
					: 0,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
				nlpSuggestedQueries: [],
			});
		} finally {
			setAnswerStreamed(true);
			setLoading(false);
		}
	};

	// New version with improved error handling using standard JS Error objects
	const processStreamResponseImprovedErrorHandling = async (
		answer: AIAnswerQueryStreamResponse,
	) => {
		const fetchStartTime = Date.now();
		let streamEventsReceived = 0;

		try {
			let next = await answer.stream.next();
			let answerFormat;
			// Local variable to track if answer is Lumina format (used for cache enablement)
			let isLuminaAnswerLocal = false;
			// Guard so the research-step failsafe only fires on the first AnswerPart
			let hasCompletedAllSteps = false;

			const processStreamMarkdownMultiTag = createMarkdownProcessorMultiTags(getLuminaComponents);
			const luminaContentMap: Record<string, string> = {};
			while (!next.done) {
				// setLoading needed here for instances where stream gets interrupted and restarts, ensuring loading state is set while stream is active
				setLoading(true);
				streamEventsReceived++;
				const streamEvent = next.value;

				if (streamEvent.type === AIAnswerQueryStreamType.AnswerType) {
					answerFormat = streamEvent.message.format;
				}
				if (
					streamEvent.type === AIAnswerQueryStreamType.GenerationType &&
					SAINFeatureControl.canRenderLumina()
				) {
					if (streamEvent.message.answer_generation_type === StreamAnswerGenerationTypes.LUMINA) {
						isLuminaAnswerLocal = true;
						setIsLuminaAnswer(true);
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.Trace) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}
					setQueryReasoning(streamEvent.message);
					// When the new FG is on and the BE sent a structured RESEARCH_STEP_*
					// trace, merge it into the local research-step state.
					if (
						streamEvent.traceData &&
						setResearchSteps &&
						(SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
							SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled())
					) {
						const traceData = streamEvent.traceData;
						setResearchSteps((prev) => mergeResearchStepTrace(prev, traceData));
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.AnswerPart) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}

					// Failsafe (mirrors Chat's `completeAllSteps`): when the answer
					// starts streaming, mark every research step complete in case the
					// BE skipped any `RESEARCH_STEP_COMPLETE` messages. Prevents the
					// shimmer animation from getting stuck on incomplete steps.
					if (
						!hasCompletedAllSteps &&
						setResearchSteps &&
						(SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
							SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled())
					) {
						hasCompletedAllSteps = true;
						setResearchSteps((prev) => {
							let mutated = false;
							const next: { [stepId: string]: SAINResearchStep } = {};
							for (const [id, step] of Object.entries(prev)) {
								if (!step.completed) {
									next[id] = { ...step, completed: true };
									mutated = true;
								} else {
									next[id] = step;
								}
							}
							return mutated ? next : prev;
						});
					}

					if (fg('kd_sain_enable_inline_lumina')) {
						const { processedText, markdownData } = processStreamMarkdownMultiTag(
							streamEvent.message.nlpResult,
						);

						if (Object.keys(markdownData).length > 0) {
							Object.entries(markdownData).forEach(([key, { content }]) => {
								luminaContentMap[key] = (luminaContentMap[key] ?? '') + content;
							});
							setLuminaContentMap({ ...luminaContentMap });
						}

						setPartialResponse((previousPartialResponse) => ({
							format: NLPSearchResultFormat.MARKDOWN,
							partialNlpResult: (previousPartialResponse?.partialNlpResult ?? '') + processedText,
						}));
					} else {
						setPartialResponse((previousPartialResponse) => {
							let previousPartialNlpResult: string =
								previousPartialResponse?.partialNlpResult || '';
							return {
								format: NLPSearchResultFormat.MARKDOWN,
								partialNlpResult: previousPartialNlpResult + streamEvent.message.nlpResult,
							};
						});
					}
				}

				if (SAINFeatureControl.canRenderLumina()) {
					if (streamEvent.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage) {
						setDynamicLayoutSubAgentMessages((prevMessages) => [
							...prevMessages,
							streamEvent.message,
						]);
					}
				}

				if (streamEvent.type === AIAnswerQueryStreamType.FinalResponse) {
					const msg = streamEvent.message;
					const authorityUiMetadata = msg.authority_ui_metadata ?? null;

					if (fg('kd_sain_enable_inline_lumina')) {
						const { boundaryContents } = extractMarkdownText(
							streamEvent.message.nlpResult ?? '',
							LUMINA_START_DELIMITER,
							LUMINA_END_DELIMITER,
							LUMINA_PLACEHOLDER_TAG,
						);

						const luminaContentMap: Record<string, string> = {};
						boundaryContents.forEach((content, index) => {
							luminaContentMap[`${LUMINA_CONTENT_PREFIX}${index}`] = content;
						});

						setLuminaContentMap(luminaContentMap);
						setData(
							msg.errorState && msg.errorState !== NLPSearchErrorState.Default
								? {
										...msg,
										streamEventsReceived,
										networkErrorDuration: Date.now() - fetchStartTime,
										authority_ui_metadata: authorityUiMetadata,
									}
								: { ...msg, authority_ui_metadata: authorityUiMetadata },
						);
					} else {
						setData(
							msg.errorState && msg.errorState !== NLPSearchErrorState.Default
								? {
										...msg,
										streamEventsReceived,
										networkErrorDuration: Date.now() - fetchStartTime,
										authority_ui_metadata: authorityUiMetadata,
									}
								: { ...msg, authority_ui_metadata: authorityUiMetadata },
						);
					}
					setPartialResponse(undefined);
					setLoading(false);
				}

				next = await answer.stream.next();
				if (abortController.signal.aborted) {
					throw new Error('Aborted');
				}
			}

			const streamEnd = next.value;
			if (abortController.signal.aborted) {
				throw new Error(streamEnd.state === 'failed' ? streamEnd.reason : 'Aborted');
			}

			if (streamEnd.state === 'complete') {
				const streamEndFinalMessage = streamEnd.value.message;

				if (fg('kd_sain_enable_inline_lumina')) {
					const { boundaryContents } = extractMarkdownText(
						streamEnd.value.message.nlpResult ?? '',
						LUMINA_START_DELIMITER,
						LUMINA_END_DELIMITER,
						LUMINA_PLACEHOLDER_TAG,
					);

					const luminaContentMap: Record<string, string> = {};
					boundaryContents.forEach((content, index) => {
						luminaContentMap[`${LUMINA_CONTENT_PREFIX}${index}`] = content;
					});

					setLuminaContentMap(luminaContentMap);
					setData(streamEndFinalMessage);
				} else {
					setData(streamEndFinalMessage);
				}

				setPartialResponse(undefined);
				// Only store to cache if not a Lumina answer
				if (isReadingAids || !isLuminaAnswerLocal) {
					setSessionItem(
						SEARCH_AI_ANSWER_CACHE_KEY,
						JSON.stringify({
							query: query,
							data: streamEndFinalMessage,
						}),
					);
				}
			} else {
				if (streamEnd.reason === 'timeout') {
					setPartialResponse(undefined);
					setData({
						errorState: NLPSearchErrorState.TimeoutError,
						errorCode: 0,
						format: null,
						disclaimer: null,
						nlpResults: [],
						uniqueSources: [],
						nlpFollowUpResults: null,
					});
				} else {
					throw new StreamFailedError(`Stream failed: ${streamEnd.reason}`, {
						details: 'details' in streamEnd ? streamEnd.details : undefined,
						errorCode: 'errorCode' in streamEnd ? streamEnd.errorCode : undefined,
						reason: streamEnd.reason,
						backendFailureType:
							'backendFailureType' in streamEnd ? streamEnd.backendFailureType : undefined,
						traceId: 'traceId' in streamEnd ? streamEnd.traceId : undefined,
					});
				}
			}
		} catch (error: unknown) {
			const networkErrorDuration = Date.now() - fetchStartTime;

			fireAnalyticsEvent(
				getStreamFetchErrorEventPayload(error, source, {
					searchSessionId,
					queryHash: sha256Hash(query),
					sourceProduct,
					isReadingAids,
					queryIntent,
				}),
			);
			setPartialResponse(undefined);

			if (error instanceof Error && error.message === 'Aborted') {
				if (fg('kd_fix_abort_errors')) {
					// Don't  set error data on client-initiated abort
					return;
				}
				setData({
					errorState: NLPSearchErrorState.NetworkError,
					networkErrorReason: 'client_aborted',
					streamEventsReceived,
					networkErrorDuration,
					format: null,
					disclaimer: null,
					nlpResults: [],
					uniqueSources: [],
					nlpFollowUpResults: null,
					nlpSuggestedQueries: [],
				});
				return;
			}

			const details = error instanceof StreamFailedError ? error.details : undefined;
			const errorCode = error instanceof StreamFailedError ? (error.errorCode ?? 0) : 0;
			const networkErrorReason = error instanceof StreamFailedError ? error.reason : undefined;
			const networkErrorDetails = error instanceof StreamFailedError ? error.details : undefined;
			const backendFailureType =
				error instanceof StreamFailedError ? error.backendFailureType : undefined;
			const networkErrorTraceId = error instanceof StreamFailedError ? error.traceId : undefined;

			setData({
				errorState:
					details === NLPSearchErrorCodes.HIPAA_ENABLED_ERROR
						? NLPSearchErrorState.HIPAAError
						: NLPSearchErrorState.NetworkError,
				errorCode,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				networkErrorTraceId,
				streamEventsReceived,
				networkErrorDuration,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
				nlpSuggestedQueries: [],
			});
		} finally {
			// Don't update loading/streamed state for aborted streams
			if (!abortController.signal.aborted || !fg('kd_fix_abort_errors')) {
				setAnswerStreamed(true);
				setLoading(false);
			}
		}
	};

	// If aiAnswer is an object with a type property -- it's a getAIAnswer
	// and we need to handle the new streaming response
	if ('type' in aiAnswer) {
		if (fg('kd_sain_improved_fetch_errors')) {
			processStreamResponseImprovedErrorHandling(aiAnswer);
		} else {
			processStreamResponse(aiAnswer);
		}
		return;
	}

	await aiAnswer
		.then((data) => {
			if ('type' in data) {
				processStreamResponse(data);
				return;
			}
			setData(data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE);
			// Only store to cache if Lumina is disabled
			if (isReadingAids || !SAINFeatureControl.canRenderLumina()) {
				setSessionItem(
					SEARCH_AI_ANSWER_CACHE_KEY,
					JSON.stringify({
						query: query,
						data: data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE,
					}),
				);
			}
			setLoading(false);
		})
		.catch((error: unknown) => {
			fireAnalyticsEvent(
				getNonStreamFetchErrorEventPayload(error, source, {
					searchSessionId,
					queryHash: sha256Hash(query),
					sourceProduct,
					isReadingAids,
					queryIntent,
				}),
			);
			const isTimeout = error instanceof DOMException && error.name === 'TimeoutError';
			setData({
				errorState: isTimeout ? NLPSearchErrorState.TimeoutError : NLPSearchErrorState.NetworkError,
				...(!isTimeout &&
					fg('kd_granular_network_errors') && {
						networkErrorReason: 'fetch_error',
						networkErrorDetails: error instanceof Error ? error.message : undefined,
					}),
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
			});
			setLoading(false);
		});
};
